import { useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";

export const SoundWaveParticles = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const particles = useMemo(() => {
    const arr = [];
    const count = 50;
    for (let i = 0; i < count; i++) {
      arr.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.5 + 0.1,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return arr;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    resize();
    window.addEventListener("resize", resize);

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      time += 0.01;

      particles.forEach((p, i) => {
        // Update position with wave motion
        p.x += p.speedX;
        p.y += p.speedY + Math.sin(time + p.phase) * 0.1;

        // Wrap around edges
        if (p.x < 0) p.x = 100;
        if (p.x > 100) p.x = 0;
        if (p.y < 0) p.y = 100;
        if (p.y > 100) p.y = 0;

        // Convert percentage to pixels
        const x = (p.x / 100) * width;
        const y = (p.y / 100) * height;

        // Draw particle with glow
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, p.size * 3);
        gradient.addColorStop(0, `rgba(224, 93, 58, ${p.opacity})`);
        gradient.addColorStop(0.5, `rgba(224, 93, 58, ${p.opacity * 0.3})`);
        gradient.addColorStop(1, "rgba(224, 93, 58, 0)");

        ctx.beginPath();
        ctx.arc(x, y, p.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw core
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224, 93, 58, ${p.opacity * 1.5})`;
        ctx.fill();
      });

      // Draw connecting lines for nearby particles
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const x1 = (p1.x / 100) * width;
          const y1 = (p1.y / 100) * height;
          const x2 = (p2.x / 100) * width;
          const y2 = (p2.y / 100) * height;
          const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.strokeStyle = `rgba(224, 93, 58, ${0.1 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      // Draw sound wave lines
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        
        for (let x = 0; x <= width; x += 10) {
          const y = height / 2 + 
            Math.sin((x / width) * Math.PI * 4 + time * 2 + i * 0.5) * 30 * (0.5 - Math.abs(x / width - 0.5));
          ctx.lineTo(x, y);
        }
        
        ctx.strokeStyle = `rgba(224, 93, 58, ${0.05 - i * 0.01})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [particles]);

  return (
    <motion.canvas
      ref={canvasRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};
